import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { AssetReadExternal, TeamReadExternal, UserReadExternal } from '@askporter/client-grieg-lyric';
import { fullName } from '@askporter/utils';
import { API } from '../api/api';

export type UnresolvedEntityType = 'user' | 'team' | 'asset';

export interface UnresolvedEntityRef {
  type: UnresolvedEntityType;
  uid: string;
}

export interface ResolvedEntityName {
  name?: string;
  isLoading: boolean;
}

const queryKeyForRef = ({ type, uid }: UnresolvedEntityRef): readonly unknown[] => [
  'task-filter-sidebar-entity-name',
  type,
  uid,
];

const fetchEntityName = async ({ type, uid }: UnresolvedEntityRef): Promise<string> => {
  switch (type) {
    case 'user': {
      const user: UserReadExternal = await API().get({ path: `users/${uid}` });
      return fullName(user?.givenName, user?.familyName);
    }
    case 'team': {
      const team: TeamReadExternal = await API().get({ path: `teams/${uid}` });
      return team?.name ?? '';
    }
    case 'asset': {
      const asset: AssetReadExternal = await API().get({ path: `assets/${uid}` });
      return asset?.title ?? '';
    }
    default:
      return '';
  }
};

export const useUnresolvedSelectedEntityNames = (refs: UnresolvedEntityRef[]): Record<string, ResolvedEntityName> => {
  const dedupedRefs = useMemo(() => {
    const seen = new Set<string>();
    return refs.filter((ref) => {
      const key = `${ref.type}:${ref.uid}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [refs]);
  const results = useQueries({
    queries: dedupedRefs.map((ref) => ({
      queryKey: queryKeyForRef(ref),
      queryFn: () => fetchEntityName(ref),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });
  return dedupedRefs.reduce<Record<string, ResolvedEntityName>>((acc, ref, index) => {
    const result = results[index];
    acc[ref.uid] = { name: result?.data || undefined, isLoading: result?.isPending ?? false };
    return acc;
  }, {});
};
